import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { StoreProvider } from "./contexts/StoreContext";
import { theme } from "./lib/utils";

// Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import CustomerApp from "./pages/customer/CustomerApp";
import BackOffice from "./pages/backoffice/BackOffice";
import StoreApp from "./pages/store/StoreApp";
import AdminPanel from "./pages/admin/AdminPanel";
import LandingPage from "./pages/LandingPage";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // Redirect based on role
    if (user?.role === 'customer') {
      return <Navigate to="/app" replace />;
    } else if (user?.role === 'store_staff') {
      return <Navigate to="/store" replace />;
    } else if (user?.role === 'store_admin') {
      return <Navigate to="/backoffice" replace />;
    } else if (user?.role === 'platform_admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

// Public Route (redirects if already logged in)
function PublicRoute({ children }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect based on role
    if (user?.role === 'customer') {
      return <Navigate to="/app" replace />;
    } else if (user?.role === 'store_staff') {
      return <Navigate to="/store" replace />;
    } else if (user?.role === 'store_admin') {
      return <Navigate to="/backoffice" replace />;
    } else if (user?.role === 'platform_admin') {
      return <Navigate to="/admin" replace />;
    }
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Customer App */}
      <Route
        path="/app/*"
        element={
          <ProtectedRoute allowedRoles={['customer', 'platform_admin']}>
            <CustomerApp />
          </ProtectedRoute>
        }
      />

      {/* Back Office (Store Admin) */}
      <Route
        path="/backoffice/*"
        element={
          <ProtectedRoute allowedRoles={['store_admin', 'platform_admin']}>
            <BackOffice />
          </ProtectedRoute>
        }
      />

      {/* Store App (Tablet - Staff) */}
      <Route
        path="/store/*"
        element={
          <ProtectedRoute allowedRoles={['store_staff', 'store_admin', 'platform_admin']}>
            <StoreApp />
          </ProtectedRoute>
        }
      />

      {/* Platform Admin */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={['platform_admin']}>
            <AdminPanel />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Initialize theme on app load
    theme.init();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <StoreProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </StoreProvider>
        </AuthProvider>
      </BrowserRouter>
      {/* Noise overlay for premium feel */}
      <div className="noise-overlay" />
    </div>
  );
}

export default App;
